import * as path from './RoutesNames';

/**
 * De qual TELA cada rota do front faz parte.
 *
 * É a ponte entre as rotas daqui e o catálogo do banco (`ACESSO_TELA.tela_chave`). Uma
 * tela tem várias rotas — listagem, cadastro, edição —, e todas pertencem à mesma
 * permissão: quem acessa Clientes acessa a listagem e o cadastro.
 *
 * POR QUE UM ARQUIVO SÓ, E NÃO UM CAMPO ESPALHADO
 *
 * Esta tabela é usada por DOIS lugares — o menu, para esconder o que a pessoa não tem, e
 * o `RequirePermissao`, para barrar a URL digitada na barra de endereço. Em dois lugares
 * elas divergiriam, e o resultado seria o pior dos dois mundos: um item escondido no menu
 * cuja rota ainda abre.
 *
 * AS CHAVES SÃO AS DO BANCO, letra por letra. Errar uma não dá erro de compilação — a
 * rota simplesmente nunca casa e a tela fica invisível para todo mundo. Por isso o
 * `ROTAS_SEM_TELA` abaixo e a conferência que ele permite.
 */
export const TELA_DA_ROTA: Record<string, string> = {
  // --- sempre liberadas: não pertencem a tela nenhuma ---
  [path.Home]: 'home',
  [path.Dashboard]: 'home',

  // --- Cadastros ---
  [path.Banco]: 'banco',
  [path.BancoCadastrar]: 'banco',
  [path.BancoAtualizar]: 'banco',

  [path.CategoriaFinanceira]: 'categoria_financeira',
  [path.CategoriaFinanceiraCadastrar]: 'categoria_financeira',
  [path.CategoriaFinanceiraAtualizar]: 'categoria_financeira',

  [path.CentroCusto]: 'centro_custo',
  [path.CentroCustoCadastrar]: 'centro_custo',
  [path.CentroCustoAtualizar]: 'centro_custo',

  [path.Cfop]: 'cfop',
  [path.CfopCadastrar]: 'cfop',
  [path.CfopAtualizar]: 'cfop',

  [path.Cliente]: 'cliente',
  [path.ClienteCadastrar]: 'cliente',
  [path.ClienteAtualizar]: 'cliente',

  [path.CondicaoPagamento]: 'condicao_pagamento',
  [path.CondicaoPagamentoCadastrar]: 'condicao_pagamento',
  [path.CondicaoPagamentoAtualizar]: 'condicao_pagamento',

  [path.ContaBancaria]: 'conta_bancaria',
  [path.ContaBancariaCadastrar]: 'conta_bancaria',
  [path.ContaBancariaAtualizar]: 'conta_bancaria',

  [path.Fabricante]: 'fabricante',
  [path.FabricanteCadastrar]: 'fabricante',
  [path.FabricanteAtualizar]: 'fabricante',

  [path.FormaPagamento]: 'forma_pagamento',
  [path.FormaPagamentoCadastrar]: 'forma_pagamento',
  [path.FormaPagamentoAtualizar]: 'forma_pagamento',

  [path.Fornecedor]: 'fornecedor',
  [path.FornecedorCadastrar]: 'fornecedor',
  [path.FornecedorAtualizar]: 'fornecedor',

  [path.GrupoCliente]: 'grupo_cliente',
  [path.GrupoClienteCadastrar]: 'grupo_cliente',
  [path.GrupoClienteAtulizar]: 'grupo_cliente',

  [path.GrupoProduto]: 'grupo_produto',
  [path.GrupoProdutoCadastrar]: 'grupo_produto',
  [path.GrupoProdutoAtualizar]: 'grupo_produto',

  [path.TipoPagamento]: 'tipo_pagamento',
  [path.TipoPagamentoCadastrar]: 'tipo_pagamento',
  [path.TipoPagamentoAtualizar]: 'tipo_pagamento',

  [path.UnidadeMedida]: 'unidade_medida',
  [path.UnidadeMedidaCadastrar]: 'unidade_medida',
  [path.UnidadeMedidaAtualizar]: 'unidade_medida',

  // Usuários, Papéis e tudo o que administra acesso compartilham a mesma permissão:
  // quem cadastra pessoa é quem decide o que ela vê. Separar criaria o estado em que
  // alguém cria usuário e não consegue dar papel a ele.
  [path.Usuario]: 'usuario',
  [path.UsuarioCadastrar]: 'usuario',
  [path.UsuarioAtualizar]: 'usuario',
  [path.Papel]: 'usuario',
  [path.PapelCadastrar]: 'usuario',
  [path.PapelAtualizar]: 'usuario',

  // --- Compras ---
  [path.DevolucaoFornecedor]: 'devolucao_fornecedor',
  [path.ImportacaoXML]: 'importacao_xml',
  [path.ImportacaoXMLCadastrar]: 'importacao_xml',

  // --- Financeiro ---
  [path.Boleto]: 'boleto',

  [path.ContasPagar]: 'contas_pagar',
  [path.ContasPagarCadastrar]: 'contas_pagar',
  [path.ContasPagarAtualizar]: 'contas_pagar',

  [path.CaixaLancamento]: 'caixa_lancamento',

  [path.ContasReceber]: 'contas_receber',
  [path.ContasReceberCadastrar]: 'contas_receber',
  [path.ContasReceberLancamento]: 'contas_receber',

  [path.PrestacaoDeContas]: 'prestacao_contas',
  [path.PrestacaoDeContasCadastro]: 'prestacao_contas',

  [path.NotaFiscal]: 'nota_fiscal',

  // --- Logística ---
  [path.MovimentacaoEstoque]: 'movimentacao_estoque',

  [path.ReajusteEstoque]: 'reajuste_estoque',
  [path.ReajusteEstoqueCadastrar]: 'reajuste_estoque',

  // --- Produtos ---
  [path.Produto]: 'produto',
  [path.ProdutoCadastrar]: 'produto',
  [path.ProdutoAtualizar]: 'produto',

  // --- Ordem de Serviço ---
  [path.OrdemServico]: 'ordem_servico',
  [path.OrdemServicoCadastrar]: 'ordem_servico',
  [path.OrdemServicoAtualizar]: 'ordem_servico',
  [path.OrdemServicoCloning]: 'ordem_servico',

  [path.Maquina]: 'maquina',
  [path.MaquinaCadastrar]: 'maquina',
  [path.MaquinaAtualizar]: 'maquina',
  [path.MaquinaConsultar]: 'maquina',

  [path.Marca]: 'marca',
  [path.MarcaCadastrar]: 'marca',
  [path.MarcaAtualizar]: 'marca',

  [path.Modelo]: 'modelo',
  [path.ModeloCadastrar]: 'modelo',
  [path.ModeloAtualizar]: 'modelo',

  // --- Vendas ---
  [path.Pedido]: 'pedido',
  [path.PedidoCadastrar]: 'pedido',
  [path.PedidoAtualizar]: 'pedido',
  [path.AdicionarProdutoCadastrar]: 'pedido',
};

/**
 * Rotas que de propósito não exigem permissão nenhuma.
 *
 * `Home` está aqui e TAMBÉM no mapa acima: ela é o destino para onde se manda quem não tem
 * acesso a algo. Se ela própria pudesse ser barrada, não haveria para onde mandar.
 */
export const ROTAS_LIVRES = new Set<string>([
  path.Login,
  path.Home,
  path.Dashboard,
  // [22/10] A tela que destrava o primeiro acesso nunca pode ser barrada: quem está nela
  // ainda não tem tela nenhuma, e exigir permissão para trocar a senha seria o beco
  // perfeito - sem acesso porque não trocou, sem trocar porque não tem acesso.
  path.TrocarSenha,
]);

/**
 * Normaliza para comparar: minúscula e sem barra no fim.
 *
 * [22/10] Existe porque ONZE entradas do Menus.ts navegam por texto literal
 * ('CategoriaFinanceira', 'Produto', 'Marca'...) em vez da constante de RoutesNames. O
 * endereço que chega na barra pode vir com outra caixa e sem a barra final, e comparar
 * cru não casaria — o guardião deixaria passar justamente essas telas.
 */
function normalizar(rota: string): string {
  const r = (rota || '').toLowerCase();
  return r.length > 1 && r.endsWith('/') ? r.slice(0, -1) : r;
}

const MAPA_NORMALIZADO: Record<string, string> = Object.fromEntries(
  Object.entries(TELA_DA_ROTA).map(([rota, tela]) => [normalizar(rota), tela])
);

const LIVRES_NORMALIZADAS = new Set(Array.from(ROTAS_LIVRES).map(normalizar));

/**
 * A tela de uma rota, ou `null` quando a rota não pertence a nenhuma.
 *
 * Rota desconhecida devolve `null` e o guardião DEIXA PASSAR. É deliberado: uma rota nova
 * que alguém esqueça de mapear aqui ficaria inacessível para todo mundo, inclusive para o
 * administrador, e o defeito apareceria como "a tela sumiu" — difícil de ligar a este
 * arquivo. O portão que não falha para o lado frouxo é o do servidor, que nega por padrão.
 *
 * O MENU NÃO USA ESTA FUNÇÃO — ele lê a `telaChave` declarada em cada item, e lá o
 * desconhecido ESCONDE. São perguntas diferentes: no menu, mostrar o que a pessoa não
 * pode usar é o próprio defeito.
 */
export function telaDaRota(rota: string): string | null {
  const r = normalizar(rota);

  if (LIVRES_NORMALIZADAS.has(r)) return null;

  if (MAPA_NORMALIZADO[r]) return MAPA_NORMALIZADO[r];

  /* Última tentativa: casar pelo começo. A rota de edição carrega o id
     ('/dashboard/cliente-cadastrar/42/detalhe'), e o mapa guarda o molde com ':id'. Sem
     isto, abrir um cadastro pela URL escaparia do guardião. */
  const prefixo = Object.keys(MAPA_NORMALIZADO).find((chave) => {
    const base = chave.split('/:')[0];
    return base.length > 1 && r.startsWith(base);
  });

  return prefixo ? MAPA_NORMALIZADO[prefixo] : null;
}
